import type { CartItem, QuotationRequest } from "./quotation";

export function percentToDecimal(percent: number): number {
  return percent / 100;
}

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function calculateDiscountedRate(listPrice: number, discountPercent: number): number {
  return roundMoney(listPrice * (1 - percentToDecimal(discountPercent)));
}

export function normalizeNonNegativeNumber(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

export function normalizePercent(value: number): number {
  return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
}

export function calculateRateFromDiscount(
  listPrice: number,
  discountPercent: number,
  hasFrlsLoading: boolean,
  hasPvcLoading: boolean
): number {
  const frlsMultiplier = hasFrlsLoading ? 1.03 : 1;
  const pvcMultiplier = hasPvcLoading ? 1.05 : 1;
  return roundMoney(
    listPrice * (1 - percentToDecimal(discountPercent)) * frlsMultiplier * pvcMultiplier
  );
}

export function calculateFinalRate(rate: number, markupPercent: number): number {
  return roundMoney(rate * (1 + percentToDecimal(markupPercent)));
}

export function calculateAmount(item: CartItem): number {
  return roundMoney(
    calculateFinalRate(item.rate ?? 0, item.markup ?? 0) * (item.quantity ?? 0)
  );
}

export function calculateSubtotal(items: CartItem[]): number {
  return roundMoney(items.reduce((total, item) => total + calculateAmount(item), 0));
}

export function calculateGst(totalWithDelivery: number): number {
  return roundMoney(totalWithDelivery * 0.18);
}

export function splitLines(value: string): string[] | undefined {
  const lines = value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  return lines.length > 0 ? lines : undefined;
}

export function buildQuotationRequest(items: CartItem[], options: {
  showPriceBreakdown?: boolean;
  deliveryCharges: number | undefined;
  customerLines: string;
  termsText: string;
}): QuotationRequest {
  return {
    items: items.map((item) => ({
      product: item.product,
      brand: item.brand,
      tag: item.tag,
      discount: 0,
      loading_frls: item.loadingFrls ? 0.03 : 0,
      loading_pvc: item.loadingPvc ? 0.05 : 0,
      quantity: item.quantity ?? 0,
      user_base_price: item.rate ?? 0,
      markup:
        item.markup !== undefined && item.markup > 0
          ? percentToDecimal(item.markup)
          : undefined
    })),
    show_price_breakdown: options.showPriceBreakdown ?? false,
    delivery_charges: options.deliveryCharges ?? 0,
    to: splitLines(options.customerLines),
    terms_and_conditions: splitLines(options.termsText)
  };
}

/** Describe the saved pricing inputs without guessing missing legacy provenance. */
export function pricingExplanation(item: CartItem): string {
  const money = (value: number) => `Rs ${roundMoney(value).toLocaleString("en-IN")}`;
  const base = item.pricingMode === "manual"
    ? `Manual rate ${money(item.rate ?? 0)}; discount and loadings not applied`
    : item.pricingMode
      ? `${item.pricingMode === "global" ? "Global" : "Row"} discount: list ${money(item.listPrice)} × (1 − ${item.discount ?? 0}%)${item.loadingFrls ? " × 1.03 FRLS" : ""}${item.loadingPvc ? " × 1.05 PVC" : ""} → ${money(item.rate ?? 0)}`
      : `Saved rate ${money(item.rate ?? 0)}; original pricing method not recorded`;
  return `${base}${item.markup ? ` × (1 + ${item.markup}% markup)` : ""} → ${money(calculateFinalRate(item.rate ?? 0, item.markup ?? 0))}/unit`;
}
